import React, { FC, ReactElement, useCallback } from 'react';
import { GestureResponderEvent, Platform, PressableProps, StyleSheet, TextStyle, View } from 'react-native';

import { Pressable } from '@/components/button/Pressable';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { LottieLoader } from './LottieLoader';
import { Text } from './TextComponent';

const { neutral900, neutral400, neutral700, neutral500, neutral300 } = designSystem.colors;

type SHAPE = 'pill' | 'rect' | 'square' | 'circle';
type HIERARCHY = 'primary' | 'primaryLilac' | 'secondary' | 'tertiary' | 'subtle';
type SIZE = 'xs' | 's' | 'm' | 'l';
type VARIANT = 'emphasis' | 'light' | 'danger' | 'alert' | 'dark' | 'outline'; // variants are exceptions from the general button component styles, avoid adding here if possible
type STYLES = { flex?: 1 };

export interface ButtonProps {
    label: string | ReactElement;
    size?: SIZE;
    shape?: SHAPE;
    hierarchy?: HIERARCHY;
    variant?: VARIANT;
    disabled?: boolean;
    loading?: boolean;
    active?: boolean;
    analyticsTag?: string;
    disableTextStyle?: TextStyle;
    textStyle?: TextStyle;
    disableContainerStyle?: PressableProps['style'];
    dropShadow?: boolean;
    loaderColors?: { main: string; second: string };
}

/*
 * The Default button is medium size, rectangle shape, secondary hierarchy - this is the most used button
 * If you need other variants to these buttons, just create a VARIANT type and add to variantsStyles
 * */
export const Button: FC<ButtonProps & PressableProps & STYLES> = ({
    label,
    shape = 'rect',
    size = 'm',
    hierarchy = 'secondary',
    active = false,
    loading = false,
    disabled = false,
    variant,
    analyticsTag,
    onPress,
    style,
    flex,
    disableTextStyle,
    disableContainerStyle,
    textStyle,
    dropShadow,
    loaderColors,
    ...props
}) => {
    const pressable = !(disabled || loading);
    const isTertiary = hierarchy === 'tertiary';
    const hoverProps =
        isWeb && pressable && !isTertiary
            ? ({ dataSet: { buttonHoverOverlay: active ? 'primary' : variant ?? hierarchy } } as PressableProps)
            : undefined;

    const onPressAction = useCallback(
        (event: GestureResponderEvent) => {
            const button = typeof label === 'string' ? label : analyticsTag;
            if (button) {
                let params = { button, isPickButton: analyticsTag?.includes('isPickButton') };
                BetrAnalytics.trackEvent(AnalyticsEvent.CLICK, params);
            }
            onPress?.(event);
        },
        [onPress, label, analyticsTag]
    );

    const getStyle = useCallback(
        ({ pressed }: { pressed: boolean }) => [
            generalStyles.wrapper,
            sizeButtonStyle[size][shape],
            loading && iconPaddings[size][shape],
            stylesHierarchy[hierarchy],
            variant && variantsStyles[variant]?.button,
            isTertiary && pressed && generalStyles.tertiaryPressed,
            disabled && disabledStyles[hierarchy],
            disabled && disableContainerStyle,
            active && activeStyles[hierarchy],
            flex && { flex: 1 },
            dropShadow && common.shadow,
            style,
        ],
        [
            size,
            shape,
            loading,
            hierarchy,
            variant,
            isTertiary,
            disabled,
            disableContainerStyle,
            active,
            flex,
            dropShadow,
            style,
        ]
    );

    const getChildren = useCallback(
        ({ pressed }: { pressed: boolean }) => {
            const hasOverlay = pressed && pressable && !isTertiary;
            const looksLikeSecondary = hierarchy === 'primary' && disabled && loading;
            const loadersColors =
                loaderColors ?? (looksLikeSecondary ? defaultLoaderColors.secondary : defaultLoaderColors[hierarchy]);
            return (
                <>
                    {hasOverlay ? <Overlay hierarchy={hierarchy} /> : null}
                    {loading ? (
                        <LottieLoader size={loaderSize[size]} color={loadersColors.main} />
                    ) : typeof label === 'string' ? (
                        <Text
                            style={[
                                generalStyles.text,
                                sizeTextStyle[size],
                                { color: stylesHierarchy[hierarchy].color },
                                variant && variantsStyles[variant]?.text,
                                disabled && { color: disabledStyles[hierarchy].color },
                                active && { color: activeStyles[hierarchy].color },
                                textStyle,
                                disabled && disableTextStyle,
                            ]}
                            variant={'buttonLabel'}
                        >
                            {label}
                        </Text>
                    ) : (
                        label
                    )}
                </>
            );
        },
        [
            active,
            disableTextStyle,
            disabled,
            hierarchy,
            isTertiary,
            label,
            loaderColors,
            loading,
            pressable,
            size,
            textStyle,
            variant,
        ]
    );

    const pressableElement = (
        <Pressable style={getStyle} disabled={disabled || loading} onPress={onPressAction} {...hoverProps} {...props}>
            {getChildren}
        </Pressable>
    );

    // iOS requires wrapping Pressable in a View for shadow to be applied
    // Android applies shadow directly to the Pressable component by using elevation
    if (Platform.OS === 'ios' && dropShadow) {
        return <View style={common.shadow}>{pressableElement}</View>;
    }

    return pressableElement;
};

const Overlay = ({ hierarchy }: { hierarchy: HIERARCHY }) => {
    const isPrimary = hierarchy === 'primary';
    const { primaryPressed, secondaryPressed } = generalStyles;
    return <View style={[generalStyles.overlay, isPrimary ? primaryPressed : secondaryPressed]} />;
};

const sizeButtonStyle = {
    xs: {
        pill: { borderRadius: 24, paddingHorizontal: 10, paddingVertical: 4 },
        rect: { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
        square: { borderRadius: 8, padding: 4, width: 28, height: 28 },
        circle: { borderRadius: 14, padding: 4, width: 28, height: 28 },
    },
    s: {
        pill: { borderRadius: 24, paddingHorizontal: 12, paddingVertical: 8 },
        rect: { borderRadius: 11, paddingHorizontal: 16, paddingVertical: 8 },
        square: { borderRadius: 10, paddingHorizontal: 4, paddingVertical: 8, width: 36, height: 36 },
        circle: { borderRadius: 18, paddingHorizontal: 4, paddingVertical: 8, width: 36, height: 36 },
    },
    m: {
        pill: { borderRadius: 24, paddingHorizontal: 16, paddingVertical: 10 },
        rect: { borderRadius: 14, paddingHorizontal: 16, paddingVertical: 12 },
        square: { borderRadius: 12, paddingHorizontal: 4, paddingVertical: 12, width: 44, height: 44 },
        circle: { borderRadius: 22, paddingHorizontal: 4, paddingVertical: 12, width: 44, height: 44 },
    },
    l: {
        pill: {
            /*does not exist*/
        },
        rect: { borderRadius: 14, paddingHorizontal: 16, paddingVertical: 14 },
        square: { borderRadius: 14, paddingHorizontal: 4, paddingVertical: 14, width: 52, height: 52 },
        circle: { borderRadius: 26, paddingHorizontal: 4, paddingVertical: 14, width: 52, height: 52 },
    },
};

const sizeTextStyle = {
    xs: { fontSize: 12, lineHeight: 20 },
    s: { fontSize: 13, lineHeight: 20, letterSpacing: -0.08 },
    m: { fontSize: 16, lineHeight: 24, letterSpacing: -0.31 },
    l: { fontSize: 17, lineHeight: 24, letterSpacing: -0.43 },
};

const stylesHierarchy = StyleSheet.create({
    primary: {
        backgroundColor: ds.colorValue.background.neutral.inverse,
        color: ds.colorValue.background.neutral.subtle,
    },
    primaryLilac: { backgroundColor: ds.colorValue.background.brand.emphasis, color: ds.colorValue.text.primary },
    secondary: { backgroundColor: ds.colorValue.background.neutral.subtle, color: ds.colorValue.text.primary },
    tertiary: { backgroundColor: 'transparent', color: ds.colorValue.text.primary },
    subtle: { backgroundColor: ds.colorValue.background.neutral.subtler, color: ds.colorValue.text.primary },
});

const disabledStyles = StyleSheet.create({
    primary: { backgroundColor: ds.colorValue.background.neutral.subtle, color: neutral900 },
    primaryLilac: { backgroundColor: ds.colorValue.background.neutral.subtle, color: neutral400 },
    secondary: { backgroundColor: ds.colorValue.background.neutral.subtle, color: neutral900 },
    tertiary: { color: neutral500 },
    subtle: { backgroundColor: ds.colorValue.background.neutral.subtler, color: neutral900 },
});

const activeStyles = StyleSheet.create({
    primary: { color: neutral900 },
    primaryLilac: { color: ds.colorValue.text.primary },
    secondary: { backgroundColor: ds.colorValue.background.neutral.inverse, color: neutral900 },
    tertiary: { backgroundColor: ds.colorValue.background.neutral.inverse, color: neutral900 },
    subtle: { backgroundColor: ds.colorValue.background.neutral.inverse, color: neutral900 },
});

const loaderSize = { xs: 12, s: 16, m: 24, l: 24 };
const defaultLoaderColors = {
    primary: { main: neutral900, second: ds.colorValue.icon.primary },
    primaryLilac: { main: neutral900, second: ds.colorValue.icon.primary },
    secondary: { main: ds.colorValue.icon.primary, second: neutral400 },
    tertiary: { main: ds.colorValue.icon.primary, second: neutral700 },
    subtle: { main: ds.colorValue.icon.primary, second: neutral400 },
};

//these paddings can be used for when the content is an icon, for example the loading spinner
const iconPaddings = {
    xs: {
        pill: { paddingHorizontal: 20, paddingVertical: 8 },
        rect: { paddingHorizontal: 20, paddingVertical: 8 },
        square: { padding: 6 },
        circle: { padding: 6 },
    },
    s: {
        pill: { paddingHorizontal: 22, paddingVertical: 10 },
        rect: { paddingHorizontal: 16, paddingVertical: 10 },
        square: { padding: 10 },
        circle: { padding: 10 },
    },
    m: {
        pill: { paddingHorizontal: 24, paddingVertical: 10 },
        rect: { paddingHorizontal: 16, paddingVertical: 12 },
        square: { padding: 12 },
        circle: { padding: 12 },
    },
    l: {
        pill: {},
        rect: { paddingHorizontal: 16, paddingVertical: 14 },
        square: { padding: 14 },
        circle: { padding: 14 },
    },
};

const variantsStyles = {
    emphasis: { button: { backgroundColor: ds.colorValue.background.brand.strong }, text: {} },
    light: { button: { backgroundColor: ds.colorValue.background.neutral.subtle }, text: {} },
    danger: { button: {}, text: { color: ds.colorValue.text.danger } },
    alert: { button: { backgroundColor: ds.colorValue.background.danger.strong }, text: { color: neutral900 } },
    dark: {
        button: { backgroundColor: ds.colorValue.background.neutral.base },
        text: { color: ds.colorValue.text.primary },
    },
    outline: {
        button: {
            borderWidth: 1,
            backgroundColor: 'transparent',
            borderColor: ds.colorValue.border.strong,
        },
        text: { color: neutral300 },
    },
};

const generalStyles = StyleSheet.create({
    text: { fontWeight: 600, textAlign: 'center' },
    wrapper: {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        borderCurve: 'continuous',
        zIndex: 1,
    },
    overlay: { position: 'absolute', top: 0, left: 0, bottom: 0, right: 0 },
    primaryPressed: { opacity: 0.2, backgroundColor: 'black' },
    secondaryPressed: { opacity: 0.1, backgroundColor: ds.colorValue.background.neutral.inverse },
    tertiaryPressed: { backgroundColor: ds.colorValue.background.neutral.subtler },
});
